import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Plus, Save, History, ArrowLeft } from 'lucide-react';
import {
  listScripts,
  getScript,
  saveScript,
  deleteScript,
  getScriptVersions,
  type ScriptResponse,
  type ScriptSummary,
  type ScriptRuntime,
} from '@/lib/api';
import { ResourceDeleteConfirm } from '@/components/common/ResourceDeleteConfirm';

interface ScriptsPageProps {
  routePrefix?: string;
}

export function ScriptsPage({
  routePrefix = '/admin/resources/scripts',
}: ScriptsPageProps): React.ReactElement {
  const { name } = useParams<{ name?: string }>();
  return name ? (
    <ScriptEditor name={name} routePrefix={routePrefix} />
  ) : (
    <ScriptListView routePrefix={routePrefix} />
  );
}

function ScriptListView({ routePrefix }: { routePrefix: string }): React.ReactElement {
  const [scripts, setScripts] = useState<ScriptSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listScripts()
      .then(r => {
        setScripts(r.scripts);
      })
      .catch((e: Error) => {
        setError(e.message);
      });
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-lg font-semibold text-text-primary">Scripts</h1>
        <Link
          to={`${routePrefix}/new`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="size-4" />
          New Script
        </Link>
      </div>
      <div className="flex-1 overflow-auto px-4 pb-4 pt-2">
        {error && <div className="rounded-md bg-error/15 p-3 text-sm text-error">{error}</div>}
        {!scripts && !error && <div className="text-sm text-text-tertiary">Loading…</div>}
        {scripts?.length === 0 && <div className="text-sm text-text-tertiary">No scripts yet.</div>}
        {scripts && scripts.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-secondary">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Runtime</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Version</th>
                <th className="py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {scripts.map(s => (
                <tr
                  key={s.name}
                  className="cursor-pointer border-b border-border hover:bg-surface/50"
                >
                  <td className="py-2 pr-4">
                    <Link
                      to={`${routePrefix}/${encodeURIComponent(s.name)}`}
                      className="text-primary"
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td className="py-2 pr-4">
                    <span className="rounded bg-surface-elevated px-2 py-0.5 text-xs">
                      {s.runtime}
                    </span>
                  </td>
                  <td className="py-2 pr-4">
                    <span className="rounded bg-surface-elevated px-2 py-0.5 text-xs">
                      {s.source}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{s.version ?? '—'}</td>
                  <td className="py-2 text-text-tertiary">{s.updatedAt?.slice(0, 19) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface VersionRow {
  version: number;
  savedAt: string;
}

function ScriptEditor({
  name,
  routePrefix,
}: {
  name: string;
  routePrefix: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const isNew = name === 'new';
  const [script, setScript] = useState<ScriptResponse | null>(null);
  const [body, setBody] = useState('');
  const [runtime, setRuntime] = useState<ScriptRuntime>('bun');
  const [timeoutMs, setTimeoutMs] = useState<string>('');
  const [scriptName, setScriptName] = useState(isNew ? '' : name);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [showVersions, setShowVersions] = useState(false);

  useEffect(() => {
    if (isNew) return;
    getScript(name)
      .then(s => {
        setScript(s);
        setBody(s.body);
        setRuntime(s.runtime);
        setTimeoutMs(s.timeoutMs?.toString() ?? '');
      })
      .catch((e: Error) => {
        setError(e.message);
      });
  }, [name, isNew]);

  const isBundled = script?.source === 'bundled';

  async function handleSave(): Promise<void> {
    if (!scriptName) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveScript(scriptName, {
        runtime,
        body,
        timeoutMs: timeoutMs ? parseInt(timeoutMs, 10) : undefined,
      });
      setScript(saved);
      if (isNew) navigate(`${routePrefix}/${encodeURIComponent(scriptName)}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    try {
      await deleteScript(scriptName);
      navigate(routePrefix);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleShowVersions(): Promise<void> {
    try {
      const r = await getScriptVersions(scriptName);
      setVersions(r.versions);
      setShowVersions(true);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <Link to={routePrefix} className="text-text-secondary hover:text-text-primary">
          <ArrowLeft className="size-4" />
        </Link>
        <input
          className="flex-1 rounded bg-surface px-2 py-1 text-sm"
          value={scriptName}
          onChange={e => {
            setScriptName(e.target.value);
          }}
          placeholder="script-name"
          disabled={!isNew}
        />
        <select
          className="rounded bg-surface px-2 py-1 text-sm"
          value={runtime}
          onChange={e => {
            setRuntime(e.target.value as ScriptRuntime);
          }}
          disabled={isBundled}
        >
          <option value="bun">bun (.ts)</option>
          <option value="uv">uv (.py)</option>
        </select>
        <input
          className="w-28 rounded bg-surface px-2 py-1 text-sm"
          placeholder="timeout ms"
          value={timeoutMs}
          onChange={e => {
            setTimeoutMs(e.target.value);
          }}
          disabled={isBundled}
        />
        <button
          onClick={handleSave}
          disabled={saving || isBundled}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          <Save className="size-4" /> Save
        </button>
        {!isNew && (
          <>
            <button
              onClick={handleShowVersions}
              className="inline-flex items-center gap-1 rounded-md bg-surface-elevated px-3 py-1 text-sm"
            >
              <History className="size-4" /> History
            </button>
            <ResourceDeleteConfirm
              resourceKind="script"
              resourceName={scriptName}
              disabled={isBundled}
              onConfirm={handleDelete}
            />
          </>
        )}
      </div>
      {error && (
        <div className="border-b border-error/30 bg-error/15 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <textarea
          className="flex-1 resize-none bg-background p-4 font-mono text-sm text-text-primary outline-none"
          value={body}
          onChange={e => {
            setBody(e.target.value);
          }}
          readOnly={isBundled}
          placeholder={runtime === 'bun' ? '// TypeScript script…' : '# Python script…'}
        />
        {showVersions && (
          <div className="w-72 overflow-auto border-l border-border bg-background p-2 text-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-semibold">Versions</span>
              <button
                onClick={() => {
                  setShowVersions(false);
                }}
                className="text-xs text-text-secondary"
              >
                close
              </button>
            </div>
            {versions.length === 0 && (
              <div className="text-text-tertiary">No previous versions</div>
            )}
            {versions.map(v => (
              <div key={v.version} className="mb-1 rounded border border-border p-2 text-xs">
                <div>v{v.version}</div>
                <div className="text-text-tertiary">{v.savedAt.slice(0, 19)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
